import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Megaphone } from 'lucide-react';
import { Table, type Column } from './Table';
import { Badge, type BadgeProps } from './Badge';
import { Modal } from './Modal';
import { Tooltip } from './Tooltip';
import { mailingListsAPI } from '../../api/endpoints';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { describeExclusions, exclusionTotal } from '../../utils/campaigns';
import type { Campaign, CampaignStatus } from '../../types';

// Campaigns per list are few; one page covers them
const PAGE_SIZE = 200;

const STATUS_BADGES: Record<CampaignStatus, { label: string; variant: BadgeProps['variant'] }> = {
  scheduled: { label: 'Scheduled', variant: 'warning' },
  sending: { label: 'Sending', variant: 'primary' },
  sent: { label: 'Sent', variant: 'success' },
  sent_with_failures: { label: 'Sent with failures', variant: 'error' },
  cancelled: { label: 'Cancelled', variant: 'default' },
};

function formatDate(value: string | null): string {
  return value ? format(new Date(value), 'dd MMM yyyy HH:mm') : '—';
}

/** When a campaign still going out should finish: its latest queued slot, read live from the queue. */
function expectedEnd(campaign: Campaign): string | null {
  const running = campaign.status === 'scheduled' || campaign.status === 'sending';
  return running ? campaign.stats.last_scheduled_at : null;
}

const ANSWERS_HINT =
  'Sent emails the venue answered: the next thing that happened with any of its contacts came in. ' +
  'Writing to a venue again hands the answer to the newer email, so this never exceeds the venues reached.';

/** "3 / 48 · 6%" — answers out of what actually went out. */
function answerSummary(campaign: Campaign): { label: string; share: string | null } {
  const { answered, sent } = campaign.stats;
  if (sent === 0) return { label: '—', share: null };
  return { label: `${answered} / ${sent}`, share: `${Math.round((answered / sent) * 100)}%` };
}

interface MailingListCampaignsProps {
  mailingListId: string;
  /**
   * The campaign whose detail is open, owned by the page's URL (`?campaign=<id>`) so that a
   * campaign's follow-up todo can link straight to it. `null` closes the detail.
   */
  openCampaignId: string | null;
  onOpenCampaignChange: (campaignId: string | null) => void;
}

/** The campaigns sent to one mailing list, newest first (docs/CAMPAIGNS.md). */
export function MailingListCampaigns({
  mailingListId,
  openCampaignId,
  onOpenCampaignChange,
}: MailingListCampaignsProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['mailing-list-campaigns', mailingListId],
    queryFn: async () => (await mailingListsAPI.listCampaigns(mailingListId, { page_size: PAGE_SIZE })).data,
    // The dispatcher ticks every minute: follow a campaign while its emails are going out
    refetchInterval: (query) =>
      query.state.data?.items.some((c) => c.status === 'scheduled' || c.status === 'sending') ? 60_000 : false,
  });
  const campaigns = useMemo(() => data?.items ?? [], [data]);

  // Derived, not held: the open campaign is whatever the URL names, which is also how a link
  // from a todo arrives here. The id resolves to a row only once the fetch above lands, and a
  // param naming a campaign of another list — or one since deleted — opens nothing.
  const openCampaign = useMemo(
    () => (openCampaignId ? campaigns.find((c) => c._id === openCampaignId) ?? null : null),
    [openCampaignId, campaigns]
  );

  const columns: Column<Campaign>[] = useMemo(
    () => [
      {
        key: 'name',
        label: 'Campaign',
        render: (row) => <span className="font-medium text-dark-text-primary">{row.name}</span>,
      },
      {
        key: 'status',
        label: 'Status',
        width: '150px',
        render: (row) => {
          const badge = STATUS_BADGES[row.status] ?? STATUS_BADGES.scheduled;
          return (
            <Badge variant={badge.variant} className="text-xs py-0">
              {badge.label}
            </Badge>
          );
        },
      },
      {
        key: 'progress',
        label: 'Progress',
        width: '200px',
        render: (row) => {
          const { sent, pending, failed, skipped, cancelled } = row.stats;
          const details = [
            pending > 0 && `${pending} queued`,
            failed > 0 && `${failed} failed`,
            skipped > 0 && `${skipped} skipped`,
            cancelled > 0 && `${cancelled} cancelled`,
          ].filter(Boolean);
          return (
            <div className="text-sm">
              <span className="text-dark-text-primary">
                {sent} / {row.recipient_count} sent
              </span>
              {details.length > 0 && <div className="text-xs text-dark-text-muted">{details.join(' · ')}</div>}
            </div>
          );
        },
      },
      {
        key: 'answers',
        label: 'Answers',
        width: '120px',
        render: (row) => {
          const { label, share } = answerSummary(row);
          return (
            <Tooltip content={ANSWERS_HINT}>
              <div className="text-sm">
                <span className="text-dark-text-primary">{label}</span>
                {share && <div className="text-xs text-dark-text-muted">{share}</div>}
              </div>
            </Tooltip>
          );
        },
      },
      {
        key: 'excluded',
        label: 'Not sent to',
        width: '130px',
        render: (row) => {
          const total = exclusionTotal(row.excluded);
          return total > 0 ? (
            <Tooltip content={describeExclusions(row.excluded)}>
              <span className="text-sm text-dark-text-secondary underline decoration-dotted">{total}</span>
            </Tooltip>
          ) : (
            <span className="text-dark-text-muted">—</span>
          );
        },
      },
      {
        key: 'created_at',
        label: 'Created',
        width: '160px',
        render: (row) => <span className="text-sm text-dark-text-secondary">{formatDate(row.created_at)}</span>,
      },
      {
        key: 'ends',
        label: 'Ends ≈',
        width: '160px',
        render: (row) => {
          const end = expectedEnd(row);
          return (
            <Tooltip content={end && 'Latest queued send. It moves if the send window or the daily limits hold emails back.'}>
              <span className="text-sm text-dark-text-secondary">{formatDate(end)}</span>
            </Tooltip>
          );
        },
      },
      {
        key: 'last_sent_at',
        label: 'Last sent',
        width: '160px',
        render: (row) => (
          <span className="text-sm text-dark-text-secondary">{formatDate(row.stats.last_sent_at)}</span>
        ),
      },
    ],
    []
  );

  return (
    <div className="animate-scaleIn mb-3">
      <div className="flex items-center gap-2 px-3 py-1.5 mb-1">
        <Megaphone className="w-4 h-4 text-dark-text-secondary" />
        <span className="text-sm font-semibold text-dark-text-secondary">Campaigns</span>
        {campaigns.length > 0 && <span className="text-xs text-dark-text-muted">({data?.total ?? 0})</span>}
      </div>
      <div className="card">
        {isLoading ? (
          <div className="p-4 text-sm text-dark-text-secondary">Loading campaigns...</div>
        ) : campaigns.length === 0 ? (
          <div className="p-4 text-sm text-dark-text-secondary">
            No campaigns yet. Use <span className="font-medium">Send Emails</span> to start one: each send is a
            campaign named after its subject.
          </div>
        ) : (
          <Table
            data={campaigns}
            columns={columns}
            searchable={false}
            onRowClick={(row) => onOpenCampaignChange(row._id)}
            emptyMessage="No campaigns"
          />
        )}
      </div>

      <Modal isOpen={openCampaign !== null} onClose={() => onOpenCampaignChange(null)} title={openCampaign?.name ?? ''} size="2xl">
        {openCampaign && (
          <div className="p-5 space-y-3">
            <div className="text-xs text-dark-text-muted space-y-0.5">
              <div>Created {formatDate(openCampaign.created_at)}</div>
              <div>
                {openCampaign.stats.sent} / {openCampaign.recipient_count} sent
                {openCampaign.stats.sent > 0 && ` · ${openCampaign.stats.answered} answered`}
                {openCampaign.stats.next_scheduled_at &&
                  ` · next send ${formatDate(openCampaign.stats.next_scheduled_at)}`}
                {expectedEnd(openCampaign) && ` · ends ≈ ${formatDate(expectedEnd(openCampaign))}`}
              </div>
              {exclusionTotal(openCampaign.excluded) > 0 && (
                <div>Not sent to: {describeExclusions(openCampaign.excluded)}</div>
              )}
            </div>
            <div
              className="email-html-content text-sm text-dark-text-primary border-t border-dark-border pt-3"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(openCampaign.body) }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}
